import { formatCurrencyForDisplay, resolveTransferTimeLabel } from '@utils'
import cn from 'classnames'
import type { PaymentOptionResponse } from 'mmfintech-backend-api/types'
import { formatMoney, tr } from 'mmfintech-commons'
import PaymentOptionIcon from '../PaymentOptionIcon'

import styles from './DepositMethodInfoCard.module.scss'

interface Props {
  method: PaymentOptionResponse
  currencyCode?: string
  variant: 'panel' | 'inline'
}

function formatPercent(value: number | undefined, emptyText: string): string {
  return typeof value === 'number' ? `${value}%` : emptyText
}

function formatFee(amount: number | undefined, currency: string | undefined, emptyText: string): string {
  return amount && amount > 0 ? formatMoney(amount, formatCurrencyForDisplay(currency)) : emptyText
}

export function DepositMethodInfoCard({ method, currencyCode, variant }: Props) {
  const { paymentMethod, feePercent, feeFlat, feeFlatCurrency, minAmount, singlePaymentMaxAmount } = method

  const title = tr(`METADATA.PAYMENT_OPTIONS.${paymentMethod}`, paymentMethod ?? '')
  const noneText = tr('FRONTEND.DEPOSIT.INFO.NONE', 'None')

  // Details with an empty value are skipped (only transfer time can be empty).
  const details = [
    {
      key: 'fee-percent',
      label: tr('FRONTEND.DEPOSIT.INFO.FEE_PERCENT', 'Fee (%)'),
      value: formatPercent(feePercent, noneText)
    },
    {
      key: 'flat-fee',
      label: tr('FRONTEND.DEPOSIT.INFO.FLAT_FEE', 'Flat fee'),
      value: formatFee(feeFlat, feeFlatCurrency, noneText)
    },
    {
      key: 'transfer-time',
      label: tr('FRONTEND.DEPOSIT.INFO.TRANSFER_TIME', 'Transfer time'),
      value: resolveTransferTimeLabel(method)
    },
    {
      key: 'min-deposit',
      label: tr('FRONTEND.DEPOSIT.INFO.MIN_DEPOSIT', 'Minimum deposit'),
      value: formatFee(minAmount, currencyCode, tr('FRONTEND.DEPOSIT.INFO.NO_MIN_DEPOSIT', 'No minimum deposit amount'))
    },
    {
      key: 'max-deposit',
      label: tr('FRONTEND.DEPOSIT.INFO.MAX_DEPOSIT', 'Maximum single payment amount'),
      value: formatFee(
        singlePaymentMaxAmount,
        currencyCode,
        tr('FRONTEND.DEPOSIT.INFO.NO_MAX_DEPOSIT', 'No max amount')
      )
    }
  ]

  return (
    <div className={cn(styles['deposit-info-card'], styles[variant])}>
      <div className={styles['deposit-info-header']}>
        <div className={styles['deposit-info-header-left']}>
          <div className={styles['deposit-info-icon']}>
            <PaymentOptionIcon paymentOption={paymentMethod} />
          </div>
          <div className={styles['deposit-info-titles']}>
            <span className={styles['deposit-info-title']}>{title}</span>
          </div>
        </div>
      </div>

      <div className={styles['deposit-info-tiles']}>
        {details
          .filter(detail => detail.value)
          .map(detail => (
            <div className={styles['deposit-info-tile']} key={detail.key}>
              <span className={styles['deposit-info-tile-label']}>{detail.label}</span>
              <span className={styles['deposit-info-tile-value']}>{detail.value}</span>
            </div>
          ))}
      </div>

      <div className={styles['deposit-info-disclaimer']}>
        {tr(
          'FRONTEND.DEPOSIT.INFO.DISCLAIMER',
          'Exact processing times may vary due to bank processing schedules, security and verification checks, public holidays, and other operational factors.'
        )}
      </div>
    </div>
  )
}
